import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { generalSettingsApi } from "@/lib/api";
import { isLeadNotificationConfigured } from "@/lib/leadStatus";

const POLL_MS = 30_000;

/**
 * Persistent app-wide warning when Lead Notification settings are incomplete.
 * Shown below the top search bar until Name + Email + Template are configured.
 */
export function LeadNotificationConfigBanner() {
  const navigate = useNavigate();
  const location = useLocation();
  const [incomplete, setIncomplete] = useState(false);
  const [checked, setChecked] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const settings = await generalSettingsApi.get();
      setIncomplete(!isLeadNotificationConfigured(settings));
    } catch {
      // Don't block the shell if settings fail to load.
    } finally {
      setChecked(true);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await refresh();
    })();
    const timer = window.setInterval(() => {
      if (!cancelled) refresh();
    }, POLL_MS);
    const onFocus = () => { if (!cancelled) refresh(); };
    const onConfigured = () => { if (!cancelled) refresh(); };
    window.addEventListener("focus", onFocus);
    window.addEventListener("sellyx:lead-notification-updated", onConfigured);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("sellyx:lead-notification-updated", onConfigured);
    };
  }, [refresh]);

  // Re-check when leaving Settings → General (user may have saved).
  useEffect(() => {
    if (location.pathname === "/settings/general") return undefined;
    refresh();
    return undefined;
  }, [location.pathname, refresh]);

  if (!checked || !incomplete) return null;

  return (
    <button
      type="button"
      onClick={() => navigate("/settings/general#lead-notification")}
      className="w-full text-left border-b border-rose-300 bg-rose-50 px-4 py-2.5 hover:bg-rose-100/80 transition-colors"
      data-testid="lead-notification-config-banner"
    >
      <div className="flex items-start gap-2 text-[12.5px] text-rose-900">
        <AlertTriangle size={15} className="mt-0.5 shrink-0 text-rose-600" />
        <div className="min-w-0">
          <div className="font-semibold">Lead Notification is not configured.</div>
          <div className="text-rose-800/90">
            Configure Name, Email and Email Template in Settings → General → Lead Notification.
          </div>
        </div>
      </div>
    </button>
  );
}

export default LeadNotificationConfigBanner;
